import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { untilDestroyed } from '@ngneat/until-destroy';

import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import { ButtonBarComponent, PanelComponent, PanelRef } from '@blaze-shared/shared-ui';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';
import { WebhookIdInputComponent } from '../../webhook-id-input/webhook-id-input.component';

import { ConnectorForm, PioneerRxConfiguration } from './pioneer-rx-connector-dialog.models';

@Component({
  selector: 'blaze-pioneer-rx-connector-dialog',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    CopyConnectorConfigEnvButtonComponent,
    DialogModule,
    EnvironmentIndicatorComponent,
    MatIconModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    PanelComponent,
    WebhookIdInputComponent,
  ],
  templateUrl: './pioneer-rx-connector-dialog.component.html',
  styleUrls: ['./pioneer-rx-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'data-connector-dialog' },
})
export class PioneerRxConnectorDialogComponent
  extends BaseConnectorDialogComponent<PioneerRxConfiguration>
  implements OnInit
{
  private readonly panelRef = inject(PanelRef);

  protected readonly webhookBaseUrl = '/PioneerRx/webhook';

  serviceName = 'PioneerRx';
  connectorForm: FormGroup<ConnectorForm>;
  isLoading = false;

  constructor() {
    super();

    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      username: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      password: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      webhookId: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    });
  }

  ngOnInit(): void {
    this.fillForm(this.getConfiguration(this.connector));
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, username, password, webhookId } = this.connectorForm.value;

    if (!connectorName || !username || !password || !webhookId) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        username,
        password,
        id: webhookId,
      },
    );

    this.isLoading = true;
    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  getConfiguration(connector?: ConnectorConfiguration): PioneerRxConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? {};

    const username = String(configuration?.username ?? '');
    const password = String(configuration?.password ?? '');
    const id = String(configuration?.id ?? '');

    return {
      connectorId,
      connectorName,
      username,
      password,
      id,
    };
  }

  fillForm(configuration: PioneerRxConfiguration): void {
    const { connectorId, connectorName, username, password, id } = configuration;
    const webhookId = id;

    this.connectorId = connectorId;

    this.connectorForm.patchValue(
      {
        connectorName,
        username,
        password,
        webhookId,
      },
      { emitEvent: false },
    );
  }
}
